import { Box, Grid, styled, Theme, Typography } from '@mui/material'
import bigPoint from 'public/images/icon/big-point.svg'
import mail from 'public/images/icon/mail.svg'
import trees from 'public/images/icon/trees.svg'

import CloudflareImage from '@ecm/ui/element/CloudflareImage'
import Color from '@ecm/ui/element/Color'
import MainContainer from '@ecm/ui/element/MainContainer'

const steps = [
  {
    text: <>Paper mail is sent to your virtual address</>,
  },
  {
    text: (
      <>
        The trained <b>Earth Class Mail team scans the incoming mail</b> items into high-quality
        PDFs
      </>
    ),
  },
  {
    text: (
      <>
        Paper mail is <b>securely shredded</b>
      </>
    ),
  },
  {
    text: (
      <>
        Scraps are <b>transported to a paper mill</b> for recycling
      </>
    ),
  },
  {
    text: (
      <>
        The shredded paper is turned into <b>“pulp” (chemicals and printing ink are removed)</b>
      </>
    ),
  },
  {
    text: (
      <>
        What was once a piece of mail is then manufactured <b>into new paper products for reuse</b>
      </>
    ),
  },
]

type StepsProps = {
  className?: string
}

const Steps = styled(({ className }: StepsProps) => (
  <Box className={className}>
    <Box className="forestBackground">
      <MainContainer sx={{ py: '3em' }}>
        <Typography variant="h3" component="h2" maxWidth="500px" margin="0 0 64px 0">
          The life of paper mail
          <br /> with Earth Class Mail
        </Typography>

        <Grid container flexDirection="column" alignItems="center" justifyContent="space-between">
          <CloudflareImage src={mail} width={100} height={100} />
          <Box py="2em" textAlign="center">
            <CloudflareImage src={bigPoint} />
            <Grid
              className="timeline"
              container
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="left"
              py={{ sm: '3em', xs: '4em' }}
              zIndex={5}
            >
              {steps.map((step, i) => (
                <Grid
                  container
                  alignContent="center"
                  className={`container ${i % 2 ? 'right' : 'left'}`}
                  key={i}
                  maxWidth={{ md: '435px', sm: '325px' }}
                  height={{ md: '100px', sm: '120px', xs: 'auto' }}
                >
                  <Box
                    flex={1}
                    padding="24px"
                    borderRadius="8px"
                    boxShadow="0px 10px 20px 0px #1729730F"
                    zIndex={15}
                    bgcolor={Color.white.normal}
                  >
                    <Typography variant="overline" color={Color.lucky.normal} margin="0">
                      STEP {i + 1}
                    </Typography>
                    <Typography
                      variant="p3"
                      color={Color.saint.normal}
                      margin="0"
                      maxWidth={{ md: '260px', xs: undefined }}
                    >
                      {step.text}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <CloudflareImage src={bigPoint} />
          </Box>
          <CloudflareImage src={trees} />
        </Grid>
      </MainContainer>
    </Box>
  </Box>
))(({ theme }: { theme: Theme }) => ({
  '.container': {
    '&:after': {
      content: "url('/images/icon/step-point.svg')",
      height: '16px',
      position: 'absolute',
      right: '-8px',
      top: '50%',
      width: '16px',
      zIndex: '1',
      [theme.breakpoints.down('sm')]: { left: 'calc(50% - 8px)', top: '0%' },
    },
    padding: '15px 30px',
    position: 'relative',
    [theme.breakpoints.down('sm')]: { padding: '25px 0px' },
  },
  '.container.left': {
    left: '-50%',
    [theme.breakpoints.down('sm')]: { left: '0%' },
  },
  '.container.right': {
    '&:after': {
      left: '-8px',
      [theme.breakpoints.down('sm')]: { left: 'calc(50% - 8px)', top: '0%' },
    },
    left: '50%',
    [theme.breakpoints.down('sm')]: { left: '0%' },
  },
  '.forestBackground': {
    backgroundImage: "url('/images/illustration/forest.svg')",
    backgroundPosition: '0 calc(60% - 60px)',
    backgroundRepeat: 'repeat-x',
  },
  '.timeline': {
    '&:after': {
      backgroundColor: Color.smoke.darker,
      bottom: '0',
      content: "''",
      height: 'auto',
      left: '50%',
      marginLeft: '-1px',
      position: 'absolute',
      top: '-4px',
      width: '2px',
    },
    position: 'relative',
  },
  background: `linear-gradient(to bottom, transparent 60%, ${Color.smoke.lightest} 40%)`,
}))

export default Steps
